import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Wifi,
  WifiOff,
  RefreshCw,
  ArrowLeft,
  RotateCcw,
  Loader2,
  Smartphone,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminSessoes() {
  const navigate = useNavigate();
  const [sessoes, setSessoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [selectedSessao, setSelectedSessao] = useState(null);
  const [resetting, setResetting] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("adminToken");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const fetchSessoes = useCallback(
    async (showRefresh = false) => {
      if (showRefresh) setRefreshing(true);

      try {
        const response = await fetch(`${API_URL}/api/admin-panel/sessions`, {
          headers: getAuthHeaders(),
        });

        if (response.status === 401) {
          localStorage.removeItem("adminToken");
          navigate("/admin");
          return;
        }

        const data = await response.json();
        if (data.success) {
          setSessoes(data.data.sessoes);
        }
      } catch (error) {
        console.error("Erro ao buscar sessões:", error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [getAuthHeaders, navigate],
  );

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchSessoes();

    const interval = setInterval(() => fetchSessoes(), 10000);
    return () => clearInterval(interval);
  }, [navigate, fetchSessoes]);

  const handleResetSession = async () => {
    setResetting(true);
    try {
      const cnpj = selectedSessao.cliente_id?.cnpj;
      if (!cnpj) {
        throw new Error("CNPJ não encontrado");
      }

      const response = await fetch(
        `${API_URL}/api/admin-panel/empresas/${cnpj}/reset-session`,
        {
          method: "POST",
          headers: getAuthHeaders(),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Erro ao resetar sessão");
      }

      setShowResetDialog(false);
      setSelectedSessao(null);
      fetchSessoes(true);
      alert("Sessão resetada com sucesso!");
    } catch (error) {
      alert(error.message);
    } finally {
      setResetting(false);
    }
  };

  const getStatusBadge = (status) => {
    const isConnected = status === "CONECTADO";
    return (
      <Badge
        className={
          isConnected
            ? "bg-green-100 text-green-700"
            : "bg-slate-100 text-slate-600"
        }
      >
        {isConnected ? (
          <Wifi className="w-3 h-3 mr-1" />
        ) : (
          <WifiOff className="w-3 h-3 mr-1" />
        )}
        {status}
      </Badge>
    );
  };

  const conectados = sessoes.filter(
    (s) => s.status_atual === "CONECTADO",
  ).length;
  const desconectados = sessoes.length - conectados;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-sessoes">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin/dashboard")}
                className="text-slate-500 hover:text-blue-600"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <div className="h-6 w-px bg-slate-300" />
              <div className="flex items-center gap-3">
                <Wifi className="w-5 h-5 text-green-600" />
                <h1 className="text-sm sm:text-lg font-semibold text-slate-800">
                  Sessões WhatsApp
                </h1>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchSessoes(true)}
              disabled={refreshing}
              className="border-slate-300 text-slate-600"
              data-testid="refresh-sessions"
            >
              <RefreshCw
                className={`w-4 h-4 sm:mr-2 ${refreshing ? "animate-spin" : ""}`}
              />
              <p className="hidden sm:flex">Atualizar</p>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total de Sessões</p>
                  <p className="text-3xl font-bold text-slate-800 mt-1">
                    {sessoes.length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Conectados</p>
                  <p className="text-3xl font-bold text-green-600 mt-1">
                    {conectados}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Wifi className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Desconectados</p>
                  <p className="text-3xl font-bold text-slate-500 mt-1">
                    {desconectados}
                  </p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <WifiOff className="w-6 h-6 text-slate-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sessions Table */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : sessoes.length === 0 ? (
              <div className="text-center py-12">
                <Smartphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Nenhuma sessão encontrada</p>
                <p className="text-slate-400 text-sm mt-1">
                  As sessões aparecerão aqui quando as empresas se conectarem
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="sessions-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left p-4 text-slate-600 font-medium">
                        Empresa
                      </th>
                      <th className="text-left p-4 text-slate-600 font-medium">
                        CNPJ
                      </th>
                      <th className="text-left p-4 text-slate-600 font-medium">
                        Status
                      </th>
                      <th className="text-left p-4 text-slate-600 font-medium">
                        Última Conexão
                      </th>
                      <th className="text-right p-4 text-slate-600 font-medium">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessoes.map((sessao) => (
                      <tr
                        key={sessao._id}
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`session-row-${sessao._id}`}
                      >
                        <td className="p-4 text-slate-800">
                          {sessao.cliente_id?.nome_empresa || "N/A"}
                        </td>
                        <td className="p-4 text-slate-600 font-mono">
                          {sessao.cliente_id?.cnpj || "N/A"}
                        </td>
                        <td className="p-4">
                          {getStatusBadge(sessao.status_atual)}
                        </td>
                        <td className="p-4 text-slate-600 text-sm">
                          {sessao.ultima_conexao
                            ? new Date(sessao.ultima_conexao).toLocaleString(
                                "pt-BR",
                              )
                            : "Nunca conectado"}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedSessao(sessao);
                                setShowResetDialog(true);
                              }}
                              className="text-amber-500 hover:text-amber-600"
                              data-testid={`reset-session-${sessao._id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Resetar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Reset Session Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent
          className="bg-white border-slate-200"
          data-testid="reset-session-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-800">
              Resetar Sessão WhatsApp
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Tem certeza que deseja resetar a sessão WhatsApp da empresa{" "}
              <strong>{selectedSessao?.cliente_id?.nome_empresa}</strong>?
              <br />
              <br />A empresa precisará escanear um novo QR Code para
              reconectar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white border-slate-300 text-slate-700 hover:bg-slate-50">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetSession}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={resetting}
              data-testid="confirm-reset-session"
            >
              {resetting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Resetar Sessão
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
